import * as React from 'react';
import { useContext, useMemo } from 'react';

import { useQuery } from '@apollo/client';
import {
  EnterpriseSetting_Key as EnterpriseSettingKey,
  EnterpriseSetting_ManagedLevelScopeType as ManagedLevelScopeType,
} from '__generated__/graphql-types';

import { useLocation } from 'js/lib/useRouter';

import {
  useEnterpriseProductFeatures,
  useEnterpriseProductFeaturesLoading,
} from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import GetAutoEnrolmentStatus from 'bundles/unified-description-page-common/queries/GetAutoEnrolmentStatus.graphql';
import type {
  GetAutoEnrolmentStatusQuery,
  GetAutoEnrolmentStatusQueryVariables,
} from 'bundles/unified-description-page-common/queries/__generated__/GetAutoEnrolmentStatus';

type EnterpriseAdminEnrollmentContextType = {
  isAdminEnrollmentEnabled: boolean;
  isLoading?: boolean;
};

const EnterpriseAdminEnrollmentContext = React.createContext<EnterpriseAdminEnrollmentContextType>({
  isAdminEnrollmentEnabled: false,
  isLoading: false,
});

const EnterpriseAdminEnrollmentDataProvider = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const features = useEnterpriseProductFeatures();
  const isFeatureFlagLoading = useEnterpriseProductFeaturesLoading();
  const thirdPartyOrganization = useThirdPartyOrganization();

  const isOptimizedLearnerFlowSettingEnabled =
    features?.enableOptimisedEnrollmentSetting && location.query.isExternal === 'true';
  const { data, error, loading } = useQuery<GetAutoEnrolmentStatusQuery, GetAutoEnrolmentStatusQueryVariables>(
    GetAutoEnrolmentStatus,
    {
      variables: {
        managedLevelScopeType: ManagedLevelScopeType.ThirdPartyOrganization,
        managedLevelScopeValue: thirdPartyOrganization?.id || '',
        enterpriseSettingKey: EnterpriseSettingKey.EnterpriseSettingKeyLmsLxpAutoEnrollmentEnabled,
      },
      errorPolicy: 'all',
      context: { clientName: 'gatewayGql' },
      skip: !isOptimizedLearnerFlowSettingEnabled,
    }
  );

  const isAdminEnrollmentEnabled = useMemo(() => {
    if (error) return false;

    const dataString = data?.EnterpriseSetting?.findByScopeAndKey?.data;
    if (!dataString) return false;

    try {
      const parsed = JSON.parse(dataString);
      return !!parsed[EnterpriseSettingKey.EnterpriseSettingKeyLmsLxpAutoEnrollmentEnabled];
    } catch {
      return false;
    }
  }, [data, error]);
  const isLoading = loading || isFeatureFlagLoading === undefined || isFeatureFlagLoading;

  return (
    <EnterpriseAdminEnrollmentContext.Provider value={{ isAdminEnrollmentEnabled, isLoading }}>
      {!isLoading && children}
    </EnterpriseAdminEnrollmentContext.Provider>
  );
};

export const useEnterpriseAdminEnrollment = () => {
  const context = useContext(EnterpriseAdminEnrollmentContext);
  return context;
};

export default EnterpriseAdminEnrollmentDataProvider;
